import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { DatePipe, NgClass, NgIf } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { Person, PersonDetails } from '@models/people.model';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { UiKitModule } from '../../../../../../shared/ui-kit/ui-kit.module';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { PeopleService } from '../../../../../../development/people.service';
import { MatButton } from '@angular/material/button';
import { PeopleActions } from '@states/people/people.action-types';
import { Store } from '@ngrx/store';
import { SharedActions } from '@states/shared/shared.action-types';
import { ConfirmDialogModel, DEFAULT_PERSON_IMAGE, UiButtonComponent } from '@ui';
import { MatTooltip } from '@angular/material/tooltip';
import { EnableEscCloseDirective } from '../../../../../../shared/directives/enable-esc-close.directive';
import { TruncateDirective } from '../../../../../../shared/directives/truncate.directive';
import { AlertEventsService } from '../../../../../../development/alert-events.service';
import { UiPreloaderComponent } from '../../../../../../shared/ui-kit/ui-preloader/ui-preloader.component';
import { PreloaderColor } from '@enums/shared.enum';
import { AuthSrcDirective } from '../../../../../../shared/directives/auth-src.directive';

@Component({
  selector: 'app-edit-existing-person-dialog',
  imports: [
    MatDialogClose,
    TranslatePipe,
    UiKitModule,
    MatIcon,
    ReactiveFormsModule,
    AuthSrcDirective,
    NgClass,
    DatePipe,
    MatTooltip,
    NgIf,
    UiButtonComponent,
    TruncateDirective,
    UiPreloaderComponent,
    MatButton,
  ],
  templateUrl: './edit-existing-person-dialog.component.html',
  styleUrl: './edit-existing-person-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [EnableEscCloseDirective],
})
export class EditExistingPersonDialogComponent implements OnInit {
  public PreloaderColor = PreloaderColor;
  protected readonly defaultPersonImage: string = DEFAULT_PERSON_IMAGE;
  public isEditing = signal<boolean>(false);

  public initialPersonData = signal<Person | null>(null);

  protected nameControl = new FormControl<string>('', [Validators.required]);

  public deleteLoader = signal<boolean>(false);

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: { person: Person; personDetails: PersonDetails; cameraName: string; locationName: string },
    private peopleService: PeopleService,
    private dialogRef: MatDialogRef<EditExistingPersonDialogComponent>,
    private store$: Store,
    private translateService: TranslateService,
    private alertEventsService: AlertEventsService,
  ) {}

  ngOnInit(): void {
    const { person } = this.data;
    if (person) {
      this.initialPersonData.set(person);
      this.nameControl.setValue(person.name);
    }
  }

  public img(gcp = true): string {
    return this.peopleService.getPersonImagePath(this.data.person, gcp);
  }

  public onEdit(value: boolean): void {
    this.isEditing.set(value);
  }

  public onCancel(): void {
    this.nameControl.setValue(this.data.person.name);
    this.isEditing.set(false);
  }

  public onRemove(): void {
    const originPerson = this.initialPersonData();
    this.deleteLoader.set(true);
    this.alertEventsService.getPersonEvents(originPerson.personId).subscribe((events) => {
      this.deleteLoader.set(false);
      if (events.length) {
        // Create HTML list of events
        const eventsList = events
          .map(
            (event) =>
              `<li><a href="/alert/edit/${event._id}" target="_blank" style="color: var(--color-link); text-decoration: underline;">${event.name}</a></li>`,
          )
          .join('');
        const eventsHtml = `<div style="margin-top: 12px;"><strong>${this.translateService.instant(
          'DELETE_PEOPLE_MODAL.REMOVE_FROM_EVENTS_INSTRUCTION',
        )}</strong><ul style="margin: 8px 0; padding-left: 20px;">${eventsList}</ul></div>`;

        this.store$.dispatch(
          SharedActions.showConfirmModal({
            options: {
              type: ConfirmDialogModel.ConfirmDialogType.INFO,
              msg: `${this.translateService.instant('DELETE_PEOPLE_MODAL.ASSOCIATED_EVENTS_MSG', {
                count: events.length,
              })}<br>${eventsHtml}</div>`,
              title: this.translateService.instant('DELETE_PEOPLE_MODAL.TITLE'),
              disableClose: true,
              params: {
                person: originPerson,
              },
            },
          }),
        );
      } else {
        this.store$.dispatch(
          SharedActions.showConfirmModal({
            options: {
              type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
              msg: this.translateService.instant('DELETE_PEOPLE_MODAL.MSG', { name: originPerson.name }),
              title: this.translateService.instant('DELETE_PEOPLE_MODAL.TITLE'),
              confirm: this.translateService.instant('CONFIRM_MODALS.COMMON_YES'),
              disableClose: true,
              params: {
                person: originPerson,
              },
            },
          }),
        );
      }
    });
  }

  public onDone(): void {
    this.dialogRef.close();
  }

  public onSave(): void {
    this.isEditing.set(false);
    this.store$.dispatch(
      PeopleActions.editPerson({ person: { ...this.initialPersonData(), name: this.nameControl.value } }),
    );
  }
}
